<script lang="ts">
  import { range } from 'lodash-es';
  import { inview } from 'svelte-inview';
  import { writable } from 'svelte/store';
  import { fade } from 'svelte/transition';

  import GalleryCard from '../../gallery/GalleryCard.svelte';

  import UserCard from '$lib/components/common/user/UserCard.svelte';
  import ExplorePost from '$lib/components/common/ExplorePost.svelte';
  import SkeletonExplorePost from '$lib/components/common/skeletons/SkeletonExplorePost.svelte';

  import scrollToLastItemOnLoad from '$lib/actions/scrollToLastItemOnLoad';

  import type {
    PeopleFiltersSchemaWithDynamic,
    PostFiltersSchemaWithDynamic,
    SortOption
  } from '$src/utils/api/feed.util';
  import type { SearchType } from 'garden-common';

  import {
    DynamicContentProvider,
    type ContentType
  } from '$src/utils/api/dynamicContentProvider.util';

  export let ids: string[] = null;
  export let filters: PostFiltersSchemaWithDynamic;
  export let peopleFilters: PeopleFiltersSchemaWithDynamic = null;
  export let sort: SortOption;
  export let peopleSort: SortOption = null;
  export let mode: 'galleries' | 'challenges' | 'posts' | 'users' | 'searchTerm' | 'all' = 'posts';
  export let type: ContentType = 'explore';

  export let searchTerm: string = null;
  export let searchType: SearchType = 'all';

  const contentProvider = new DynamicContentProvider(ids, type);

  let activeQuery = contentProvider.activeQuery;
  $: results = contentProvider.results;

  function update_sort_filters() {
    contentProvider.setFilters(filters);
    contentProvider.setSort(sort);
    contentProvider.resetActiveQuery();
  }

  function update_people_sort_filters() {
    contentProvider.setPeopleFilters(peopleFilters);
    contentProvider.setPeopleSort(peopleSort);
    contentProvider.resetActiveQuery();
  }

  function update_all_sort_filters() {
    contentProvider.setAllFilters(filters);
    contentProvider.setAllSort(sort);
    contentProvider.resetActiveQuery();
  }

  function update_mode() {
    contentProvider.setSearchTerm(searchTerm);
    contentProvider.setMode(mode);
    contentProvider.resetActiveQuery();
  }

  function update_searchType() {
    contentProvider.setSearchType(searchType);
  }

  $: if ((sort, filters)) {
    update_sort_filters();
    update_all_sort_filters();
  }
  // $: sort, filters, update_sort_filters(), update_all_sort_filters();

  $: {
    if (peopleSort && peopleFilters) {
      update_people_sort_filters();
    }
  }
  $: mode, searchTerm, update_mode();
  $: searchType, update_searchType();

  const lastItemContainer = writable<HTMLElement>();
</script>

<div
  class="overflow-auto w-full flex gap-x-4 flex-col px-2 lg:px-4 tc-det"
  use:scrollToLastItemOnLoad={{ arrToWatch: results, lastItemContainer }}
>
  <div class="pt-20 lg:pt-8">
    <slot name="title" />
  </div>
  {#if $activeQuery.isSuccess}
    {#if mode === 'all' || mode === 'searchTerm'}
      <div in:fade|global class="grid lg:grid-cols-3 gap-4">
        {#each $results as result}
          <div bind:this={$lastItemContainer}>
            {#if result.type === 'post'}
              <ExplorePost post={result.data} />
            {:else if result.type === 'user'}
              <UserCard user={result.data} />
            {:else if result.type === 'gallery'}
              <GalleryCard variant="vertical" gallery={result.data} />
            {/if}
          </div>
        {/each}
      </div>
    {:else if mode === 'posts'}
      <div in:fade|global class="grid lg:grid-cols-3 gap-4">
        {#each $results.filter(r => r.type === 'post') as post}
          {#if post.type === 'post'}
            <!--Just for TS-->
            <ExplorePost
              {type}
              on:splicePost={() => {
                if ($results.length === 0) return [];

                var index = $results.findIndex(function (i) {
                  return $results.data._id === this.post._id;
                });

                if (index !== -1) $results.splice(index, 1);

                $results = $results;
              }}
              post={post.data}
            />
          {/if}
        {/each}
      </div>
    {:else if mode === 'users'}
      <div in:fade|global class="grid lg:grid-cols-3 gap-4">
        {#each $results.filter(r => r.type === 'user') as user}
          {#if user.type === 'user'}
            <!-- Just for TS -->
            <UserCard user={user.data} displayFollow />
          {/if}
        {/each}
      </div>
    {/if}
  {/if}

  {#if ($activeQuery.hasNextPage || !$activeQuery.data?.pages.length) && !$activeQuery.isError}
    <div
      class="grid lg:grid-cols-3 gap-4 mt-4 "
      use:inview
      on:inview_enter={() => {
        contentProvider.fetchNextPage();
      }}
    >
      {#each range($activeQuery.data ? 3 : 9) as _}
        <SkeletonExplorePost />
      {/each}
    </div>
  {/if}

  {#key $results}
    <div class="font-bold text-center w-full text-gray-400 mt-16 h-80 mb-5">
      {#if $activeQuery.isError}
        Load failed.
      {:else if !$activeQuery.hasNextPage}
        No more posts to show.
      {/if}
    </div>
  {/key}
</div>
